import React from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  IconButton,
  Flex,
  Avatar,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { FaMoneyBillWave } from 'react-icons/fa';

const SupplierList = ({ suppliers, onEdit, onDelete, onViewDebt }) => {
  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');

  return (
    <>
      {suppliers.map((supplier) => (
        <Box
          key={supplier.id}
          bg={cardBg}
          borderRadius="xl"
          border="1px solid"
          borderColor={borderColor}
          p={6}
          boxShadow={`0 10px 15px -3px ${shadowColor}`}
          position="relative"
          overflow="hidden"
          transition="all 0.3s ease"
          _hover={{ 
            transform: "translateY(-4px)",
            boxShadow: `0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`,
          }}
        >
          {/* Gradient accent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="4px"
            bg="linear-gradient(90deg, #667eea, #764ba2)"
          />
          
          <Flex justify="space-between" align="flex-start">
            <HStack spacing={4} align="flex-start" flex="1">
              <Avatar 
                name={supplier.name} 
                size="lg"
                bg="linear-gradient(135deg, #667eea, #764ba2)"
                color="white"
              />
              <VStack align="flex-start" spacing={2} flex="1">
                <Text 
                  fontWeight="bold" 
                  fontSize="lg"
                  color={textColor}
                  noOfLines={1}
                >
                  🏭 {supplier.name || 'N/A'}
                </Text>
                <VStack align="flex-start" spacing={1}>
                  <Text fontSize="sm" color={subtleTextColor}>
                    📧 Email: {supplier.email || 'N/A'}
                  </Text>
                  <Text fontSize="sm" color={subtleTextColor}>
                    📞 Телефон: {supplier.phone_number || 'N/A'}
                  </Text>
                  <Text fontSize="sm" color={subtleTextColor}>
                    🏢 ЄДРПОУ: {supplier.edrpou_code || 'N/A'}
                  </Text>
                  <Text fontSize="sm" color={subtleTextColor} noOfLines={1}>
                    📍 Адреса: {supplier.address || 'N/A'}
                  </Text>
                  <Text fontSize="sm" color={subtleTextColor} noOfLines={1}>
                    🏦 Банк: {supplier.bank_name || 'N/A'}
                  </Text>
                  <Text fontSize="sm" color={subtleTextColor}>
                    💳 Рахунок: {supplier.bank_number || 'N/A'}
                  </Text>
                </VStack>
                <VStack align="flex-start" spacing={1} pt={2}>
                  <HStack>
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>
                      💰 Борг:
                    </Text>
                    <Badge
                      colorScheme={parseFloat(supplier.debt_amount || 0) > 0 ? 'red' : 'green'}
                      fontSize="sm"
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      {parseFloat(supplier.debt_amount || 0).toFixed(2)} грн
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" color={subtleTextColor}>
                    📅 Створено: {supplier.created_at || 'Н/Д'}
                  </Text>
                  <Text fontSize="xs" color={subtleTextColor}>
                    🔄 Оновлено: {supplier.updated_at || 'N/A'}
                  </Text>
                </VStack>
              </VStack>
            </HStack>
            
            {/* Action buttons positioned at bottom right */}
            <VStack spacing={2} position="absolute" bottom={4} right={4}>
              <IconButton
                icon={<EditIcon />}
                size="sm"
                borderRadius="lg"
                bg="linear-gradient(135deg, #4facfe, #00f2fe)"
                color="white"
                boxShadow="0 4px 6px rgba(79, 172, 254, 0.3)"
                _hover={{
                  bg: "linear-gradient(135deg, #3d8bfe, #00d4ff)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 8px rgba(79, 172, 254, 0.4)",
                }}
                onClick={() => onEdit(supplier)}
                aria-label="Edit Supplier"
              />
              <IconButton
                icon={<FaMoneyBillWave />}
                size="sm"
                borderRadius="lg"
                bg="linear-gradient(135deg, #feca57, #ff9ff3)"
                color="white"
                boxShadow="0 4px 6px rgba(254, 202, 87, 0.3)"
                _hover={{
                  bg: "linear-gradient(135deg, #feb236, #ff8ce5)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 8px rgba(254, 202, 87, 0.4)",
                }}
                onClick={() => onViewDebt(supplier)}
                aria-label="View Debt"
              />
              <IconButton
                icon={<DeleteIcon />}
                size="sm"
                borderRadius="lg"
                bg="linear-gradient(135deg, #f093fb, #f5576c)"
                color="white"
                boxShadow="0 4px 6px rgba(240, 147, 251, 0.3)"
                _hover={{
                  bg: "linear-gradient(135deg, #e084fc, #f4466b)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 8px rgba(240, 147, 251, 0.4)",
                }}
                onClick={() => onDelete(supplier)}
                aria-label="Delete Supplier"
              />
            </VStack>
          </Flex>
        </Box>
      ))}
    </>
  );
};

export default SupplierList;
